/*
 * Usage:
 * - This file exposes window.publishShopifyEvent(eventName, payload[, logFn]).
 * - Call publishShopifyEvent from theme JS to send custom events to Shopify Pixels.
 * - Example: publishShopifyEvent('event_name', { key: 'value' });
 */
(function () {
  // Global helper to publish events for Shopify custom pixels.
  function publishShopifyEvent(eventName, payload, logFn) {
    if (window.Shopify?.analytics?.publish) {
      Shopify.analytics.publish(eventName, payload);
      if (typeof logFn === "function") logFn(`Shopify.analytics.publish: ${eventName}`, payload);
      return true;
    }
    if (typeof logFn === "function") logFn("⚠️ Shopify.analytics.publish not available yet.");
    return false;
  }

  window.publishShopifyEvent = publishShopifyEvent;

  function getDynamicScrollThresholdPercent() {
    var body = document.body;
    var documentElement = document.documentElement;

    var documentHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      documentElement.clientHeight,
      documentElement.scrollHeight,
      documentElement.offsetHeight
    );

    var minHeightPx = 2000;
    var maxHeightPx = 18000;
    var maxPercent = 90;
    var minPercent = 50;

    if (documentHeight <= minHeightPx) return maxPercent;
    if (documentHeight >= maxHeightPx) return minPercent;

    var normalizedHeight = (documentHeight - minHeightPx) / (maxHeightPx - minHeightPx);
    var dropoffGamma = 0.5; // smaller = steeper early drop
    var thresholdPercentCalc =
      maxPercent + (minPercent - maxPercent) * Math.pow(normalizedHeight, dropoffGamma);

    return Math.round(thresholdPercentCalc);
  }

  function getScrollProgressPercent() {
    var documentElement = document.documentElement;
    var scrollTopPx = window.pageYOffset || documentElement.scrollTop || 0;
    var viewportHeightPx = window.innerHeight || documentElement.clientHeight || 0;
    var scrollHeightPx = documentElement.scrollHeight || 0;
    if (!scrollHeightPx) return 0;
    var scrolledPercent = ((scrollTopPx + viewportHeightPx) / scrollHeightPx) * 100;
    return Math.min(100, Math.max(0, Math.round(scrolledPercent)));
  }

  function trackShopifyPageLoad() {
    publishShopifyEvent("shopify_page_load", {
      timestamp: Date.now(),
      scrollHeight: document.documentElement.scrollHeight,
      page_path: window.location.pathname,
    });
  }

  function trackScrollDynamic() {
    var hasFired = false;
    var thresholdPercent = getDynamicScrollThresholdPercent();

    function handleScrollDynamic() {
      if (hasFired) return;
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      var currentScrollPercent = getScrollProgressPercent();
      if (currentScrollPercent >= thresholdPercent) {
        hasFired = true;
        publishShopifyEvent("scroll_dynamic", {
          timestamp: Date.now(),
          thresholdPercent: thresholdPercent,
          scrollPercent: currentScrollPercent,
          scrollHeight: document.documentElement.scrollHeight,
          scrollTop: scrollTop,
          viewportHeight: window.innerHeight || document.documentElement.clientHeight || 0,
        });
        window.removeEventListener("scroll", handleScrollDynamic);
        window.removeEventListener("resize", handleScrollDynamic);
      }
    }

    window.addEventListener("scroll", handleScrollDynamic, { passive: true });
    window.addEventListener("resize", handleScrollDynamic);
    handleScrollDynamic();
  }

  function trackLinkClicks() {
    document.addEventListener(
      "click",
      function (event) {
        var link = event.target && event.target.closest ? event.target.closest("a") : null;
        if (!link) return;

        var href = link.getAttribute("href") || "";
        // Skip non-navigation anchors: empty, in-page (#...) and pseudo-protocols.
        // mailto: is intentionally kept — email-link clicks are a tracked signal.
        if (!href || href.charAt(0) === "#" || /^(javascript|tel):/i.test(href)) return;

        var resolvedUrl = link.href || href;
        var isOutbound = link.hostname ? link.hostname !== window.location.hostname : false;

        publishShopifyEvent("link_click", {
          timestamp: Date.now(),
          href: href,
          url: resolvedUrl,
          text: (link.textContent || "").trim().slice(0, 120),
          is_outbound: isOutbound,
          page_path: window.location.pathname,
        });
      },
      true
    );
  }

  function trackActiveMilestones() {
    // Ordered ascending; each fires once when accumulated active time crosses it.
    // Active time accumulates across page loads within the same browser tab (a
    // per-tab "session") via sessionStorage, so a milestone reflects total active
    // engagement across the visit rather than a single page-load instance. Each
    // milestone therefore fires at most once per tab session, on whichever page the
    // cumulative total crosses its threshold. sessionStorage self-clears on tab close.
    var MILESTONES = [
      { targetMs: 10000, eventName: "active_10s", fired: false },
      { targetMs: 30000, eventName: "active_30s", fired: false },
      { targetMs: 60000, eventName: "active_60s", fired: false },
      { targetMs: 120000, eventName: "active_120s", fired: false },
    ];
    // Longest silent gap between inputs still counted as active. Tuned to ~10s so genuine
    // reading pauses (scroll, read a product description, scroll again) count as engagement;
    // gaps longer than this are treated as idle and dropped.
    var MAX_ACTIVITY_GAP_MS = 10000;
    var ACTIVITY_SAMPLE_MS = 250;
    var SESSION_STORAGE_KEY = "gar_active_session";

    var debugEnabled = false;
    try {
      debugEnabled = new URLSearchParams(window.location.search).get("gar_debug") === "true";
    } catch (e) {}
    function logDebug() {
      if (!debugEnabled) return;
      console.log.apply(console, ["⏱️ active-milestones:"].concat([].slice.call(arguments)));
    }

    var allFired = false;
    var activeDurationMs = 0; // cumulative across the tab session (seeded from storage)
    var pageActiveDurationMs = 0; // active time accrued on this page load only
    var lastActivityAt = null;

    // --- Per-tab session persistence. Degrades to in-memory (per-page) on any failure. ---
    function loadSessionState() {
      try {
        var parsed = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) || "null");
        if (!parsed) return null;
        return {
          activeDurationMs:
            typeof parsed.activeDurationMs === "number" && parsed.activeDurationMs >= 0
              ? parsed.activeDurationMs
              : 0,
          fired: Array.isArray(parsed.fired) ? parsed.fired : [],
        };
      } catch (e) {
        return null;
      }
    }

    function saveSessionState() {
      try {
        var firedNames = [];
        for (var i = 0; i < MILESTONES.length; i++) {
          if (MILESTONES[i].fired) firedNames.push(MILESTONES[i].eventName);
        }
        sessionStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify({ activeDurationMs: activeDurationMs, fired: firedNames })
        );
      } catch (e) {}
    }

    // Seed the in-memory accumulator + fired flags from any prior pages this session.
    var persisted = loadSessionState();
    if (persisted) {
      activeDurationMs = persisted.activeDurationMs;
      for (var s = 0; s < MILESTONES.length; s++) {
        if (persisted.fired.indexOf(MILESTONES[s].eventName) !== -1) MILESTONES[s].fired = true;
      }
    }

    // If every milestone already fired earlier this session, do nothing on this page.
    allFired = true;
    for (var a = 0; a < MILESTONES.length; a++) {
      if (!MILESTONES[a].fired) {
        allFired = false;
        break;
      }
    }
    if (allFired) {
      logDebug("all milestones already fired this session; skipping listeners", {
        activeDurationMs: activeDurationMs,
      });
      return;
    }

    logDebug("init", {
      activeDurationMs: activeDurationMs,
      fired: persisted ? persisted.fired : [],
    });

    function isTrackableActivityState() {
      return !document.hidden && typeof document.hasFocus === "function" && document.hasFocus();
    }

    function resetActivityWindow() {
      lastActivityAt = null;
    }

    function maybePublishActiveEvents() {
      if (allFired) return;

      var firedSomething = false;
      for (var i = 0; i < MILESTONES.length; i++) {
        var milestone = MILESTONES[i];
        if (milestone.fired || activeDurationMs < milestone.targetMs) continue;

        milestone.fired = true;
        firedSomething = true;
        publishShopifyEvent(
          milestone.eventName,
          {
            timestamp: Date.now(),
            activeDurationMs: activeDurationMs, // cumulative session total (back-compat field)
            pageActiveDurationMs: pageActiveDurationMs,
            milestoneMs: milestone.targetMs,
            page_path: window.location.pathname,
          },
          logDebug
        );
      }

      if (firedSomething) saveSessionState();

      if (MILESTONES[MILESTONES.length - 1].fired) {
        allFired = true;
        removeListeners();
      }
    }

    function handleActivity() {
      if (allFired || !isTrackableActivityState()) {
        resetActivityWindow();
        return;
      }

      var now = Date.now();

      if (lastActivityAt !== null && now - lastActivityAt < ACTIVITY_SAMPLE_MS) return;

      if (lastActivityAt !== null) {
        var deltaMs = now - lastActivityAt;
        if (deltaMs > 0 && deltaMs <= MAX_ACTIVITY_GAP_MS) {
          activeDurationMs += deltaMs;
          pageActiveDurationMs += deltaMs;
          saveSessionState(); // persist the running cumulative total (throttled by the sample gate)
        }
      }

      lastActivityAt = now;
      maybePublishActiveEvents();
    }

    function handleVisibilityOrFocusChange() {
      if (!isTrackableActivityState()) {
        resetActivityWindow();
        saveSessionState(); // flush when the tab is backgrounded/blurred
        return;
      }
      // Regained focus/visibility: re-anchor so the gap from returning to the tab to the
      // first interaction is counted (capped by MAX_ACTIVITY_GAP_MS), mirroring the init
      // anchor. Safe — lastActivityAt was nulled on blur, so nothing pending is discarded.
      lastActivityAt = Date.now();
    }

    function handleSessionFlush() {
      // pagehide covers navigations/bfcache where beforeunload is unreliable.
      saveSessionState();
    }

    function removeListeners() {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      window.removeEventListener("pointerdown", handleActivity);
      window.removeEventListener("focus", handleVisibilityOrFocusChange);
      window.removeEventListener("blur", handleVisibilityOrFocusChange);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocusChange);
      window.removeEventListener("pagehide", handleSessionFlush);
    }

    // Anchor the activity clock at init so the gap from page-ready to the first input
    // event is counted (capped by MAX_ACTIVITY_GAP_MS), instead of being silently lost
    // on every page. Only anchor when the tab is actually engaged right now; if it loaded
    // hidden/unfocused, stay idle until the first real interaction.
    if (isTrackableActivityState()) lastActivityAt = Date.now();

    window.addEventListener("mousemove", handleActivity, { passive: true });
    window.addEventListener("scroll", handleActivity, { passive: true });
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("touchstart", handleActivity, { passive: true });
    window.addEventListener("pointerdown", handleActivity, { passive: true });
    window.addEventListener("focus", handleVisibilityOrFocusChange);
    window.addEventListener("blur", handleVisibilityOrFocusChange);
    document.addEventListener("visibilitychange", handleVisibilityOrFocusChange);
    window.addEventListener("pagehide", handleSessionFlush);
  }

  function handleCapturedEmail(input, source, extra = {}) {
    var email = input.value.trim();
    if (!email) return;

    // console.log("📧 Email captured:", {
    //   source: source,
    //   id: input.id,
    //   name: input.name,
    //   email: email,
    //   ...extra,
    // });

    if (window.Shopify?.analytics?.publish) {
      Shopify.analytics.publish("email_captured", {
        email: email,
        source: source,
        field_id: input.id,
        field_name: input.name,
        ref_url: window.location.href,
        ...extra,
      });
    }
  }

  /**
   * 1. Normal DOM fields: Omnisend, regular inputs
   */
  document.addEventListener("focusout", function (event) {
    var input = event.target;

    if (!(input instanceof HTMLInputElement)) return;

    var isOmnisendEmail =
      input.matches('input[name="emailField"]') ||
      input.matches('input[type="email"]') ||
      input.matches('input[autocomplete="email"]');

    if (!isOmnisendEmail) return;

    handleCapturedEmail(input, "normal_dom");
  });

  /**
   * 2. Shopify Forms inside Shadow DOM
   */
  function attachShopifyFormsEmailCapture() {
    document.querySelectorAll("shopify-forms-embed").forEach(function (host) {
      if (host.dataset.emailFocusoutAttached === "true") return;

      var root = host.shadowRoot;
      if (!root) return;

      host.dataset.emailFocusoutAttached = "true";

      root.addEventListener("focusout", function (event) {
        var input = event.target;

        if (!(input instanceof HTMLInputElement)) return;

        var isShopifyEmail =
          input.matches("input#email") ||
          input.matches('input[data-testid="field-email"]') ||
          input.matches('input[inputmode="email"]') ||
          input.matches('input[type="email"]');

        if (!isShopifyEmail) return;

        handleCapturedEmail(input, "shopify_form_shadow_dom", {
          form_host_id: host.id,
        });
      });

      console.log("✅ Attached Shopify Forms listener:", host);
    });
  }

  attachShopifyFormsEmailCapture();

  new MutationObserver(attachShopifyFormsEmailCapture).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // Active-time tracking must start as early as possible. This script is `defer`, so the
  // DOM is already parsed here — waiting for `window.load` (which blocks on all images/
  // fonts) would miss the scrolling the user does on the visually-ready-but-not-loaded
  // page, and re-lose that window on every navigation. The rest stay on `load` because
  // they depend on the final document height.
  trackActiveMilestones();

  window.addEventListener("load", function () {
    trackShopifyPageLoad();
    trackScrollDynamic();
    trackLinkClicks();
  });
})();
